import React, { useState } from 'react';
import {
  MantineProvider,
  AppShell,
  Text,
  Button,
  Stack,
  Title,
  Burger,
  createTheme,
  rem,
  Box
} from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications, Notifications } from '@mantine/notifications';
import 'dayjs/locale/zh-tw';
import {
  IconLogout,
  IconCalculator,
  IconChartBar,
  IconCalendarEvent,
  IconTable,
  IconSettings
} from '@tabler/icons-react';
import { ProvideAuth, ElderlyPrivateRoute } from '../utils/auth';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase-init';
import ElderlyKeyinIndex from '../pages/keyin/ElderlyKeyinIndex';
import DailyEdit from '../pages/keyin/DailyEdit';
import ElderlyBoardIndex from '../pages/board/ElderlyBoardIndex';
import DetailedTable from '../pages/board/DetailedTable';
import FestivalManager from '../pages/admin/FestivalManager';
import ErrorBoundary from '../components/ErrorBoundary';

// 為老人設計的主題 - 大字體、高對比度
const elderlyTheme = createTheme({
  fontSizes: {
    xs: rem(16),
    sm: rem(18),
    md: rem(20),
    lg: rem(24),
    xl: rem(28)
  },
  lineHeights: {
    xs: '1.8',
    sm: '1.8',
    md: '1.8',
    lg: '1.8',
    xl: '1.8'
  },
  spacing: {
    xs: rem(12),
    sm: rem(16),
    md: rem(24),
    lg: rem(32),
    xl: rem(48)
  },
  colors: {
    // 高對比度的藍色系
    blue: [
      '#e7f5ff',
      '#d0ebff',
      '#a5d8ff',
      '#74c0fc',
      '#339af0',
      '#228be6',
      '#1c7ed6',
      '#1971c2',
      '#1864ab',
      '#145b8b'
    ],
    // 高對比度的綠色系
    green: [
      '#ebfbee',
      '#d3f9d8',
      '#b2f2bb',
      '#8ce99a',
      '#69db7c',
      '#51cf66',
      '#40c057',
      '#37b24d',
      '#2f9e44',
      '#2b8a3e'
    ],
    // 高對比度的紅色系
    red: [
      '#fff5f5',
      '#ffe3e3',
      '#ffc9c9',
      '#ffa8a8',
      '#ff8787',
      '#ff6b6b',
      '#fa5252',
      '#f03e3e',
      '#e03131',
      '#c92a2a'
    ]
  },
  primaryColor: 'blue',
  primaryShade: { light: 6, dark: 8 },
  defaultRadius: 'md'
});

function ElderlyContentApp() {
  const [opened, { toggle }] = useDisclosure(false);
  const [activeSection, setActiveSection] = useState('daily');

  const sections = {
    keyin: {
      label: '記帳輸入',
      icon: IconCalculator,
      component: <ElderlyKeyinIndex />
    },
    daily: {
      label: '當天編輯',
      icon: IconCalendarEvent,
      component: <DailyEdit />
    },
    board: {
      label: '年度總覽',
      icon: IconChartBar,
      component: <ElderlyBoardIndex />
    },
    detailed: {
      label: '收支明細表',
      icon: IconTable,
      component: <DetailedTable />
    },
    festival: {
      label: '節日管理',
      icon: IconSettings,
      component: <FestivalManager />
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        notifications.show({
          title: '登出成功',
          message: '您已安全登出',
          color: 'green'
        });
      })
      .catch(error => {
        notifications.show({
          title: '登出失敗',
          message: error.message,
          color: 'red'
        });
      });
  };

  return (
    <AppShell
      navbar={{
        width: { base: 280, sm: 300 },
        breakpoint: 'sm',
        collapsed: { mobile: !opened, desktop: false }
      }}
      header={{ height: { base: 56, sm: 70 } }}
      padding="xs"
      withBorder
    >
      <AppShell.Header p={0}>
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            alignItems: 'center',
            height: '100%',
            padding: `0 ${rem(12)}`
          }}
        >
          {/* 左側區域：漢堡選單 */}
          <Box style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box hiddenFrom="sm">
              <Burger opened={opened} onClick={toggle} size="sm" />
            </Box>
          </Box>

          {/* 中間區域：Logo + 標題 */}
          <Box
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: rem(8) }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <img
                src="/logo.svg"
                alt="Logo"
                style={{
                  height: rem(32),
                  width: 'auto',
                  display: 'block'
                }}
              />
            </Box>
            <Box visibleFrom="sm">
              <Title
                order={2}
                size="lg"
                c="blue.7"
                fw={600}
                style={{
                  display: opened ? 'none' : 'block',
                  fontSize: rem(18),
                  lineHeight: 1.2,
                  margin: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                家庭記帳本
              </Title>
            </Box>
            <Box hiddenFrom="sm">
              <Title
                order={2}
                size="md"
                c="blue.7"
                fw={600}
                style={{
                  display: opened ? 'none' : 'block',
                  fontSize: rem(14),
                  lineHeight: 1.2,
                  margin: 0,
                  whiteSpace: 'nowrap'
                }}
              >
                記帳本
              </Title>
            </Box>
          </Box>

          {/* 右側區域：登出按鈕 */}
          <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box visibleFrom="sm">
              <Button
                variant="subtle"
                color="red"
                size="sm"
                leftSection={<IconLogout size={18} />}
                onClick={handleLogout}
                styles={{
                  root: {
                    fontSize: rem(14),
                    height: rem(36),
                    minWidth: rem(70),
                    padding: `${rem(6)} ${rem(12)}`,
                    border: 'none'
                  }
                }}
              >
                登出
              </Button>
            </Box>
            <Box hiddenFrom="sm">
              <Button
                variant="subtle"
                color="red"
                size="sm"
                onClick={handleLogout}
                p={0}
                styles={{
                  root: {
                    fontSize: rem(14),
                    height: rem(36),
                    width: rem(36),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: rem(18)
                  }
                }}
              >
                <IconLogout size={18} />
              </Button>
            </Box>
          </Box>
        </Box>
      </AppShell.Header>

      <AppShell.Navbar p={{ base: 'sm', sm: 'md' }}>
        <Stack gap={{ base: 'sm', sm: 'md' }}>
          <Text size="lg" fw={600} c="blue.8" ta="center" mb="md">
            功能選單
          </Text>

          {Object.entries(sections).map(([key, section]) => {
            const Icon = section.icon;
            return (
              <Button
                key={key}
                variant={activeSection === key ? 'filled' : 'light'}
                color="blue"
                size="xl"
                h={60}
                leftSection={<Icon size={22} />}
                onClick={() => {
                  setActiveSection(key);
                  if (opened) toggle(); // 手機版點選後關閉選單
                }}
                styles={{
                  root: {
                    fontSize: rem(18),
                    fontWeight: 600
                  },
                  inner: {
                    justifyContent: 'flex-start'
                  }
                }}
                fullWidth
              >
                {section.label}
              </Button>
            );
          })}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>{sections[activeSection]?.component}</AppShell.Main>
    </AppShell>
  );
}

function ElderlyApp() {
  return (
    <ErrorBoundary>
      <MantineProvider theme={elderlyTheme}>
        <DatesProvider
          settings={{
            locale: 'zh-tw',
            firstDayOfWeek: 0,
            weekendDays: [0, 6],
            timezone: 'Asia/Taipei'
          }}
        >
          <Notifications />
          <ErrorBoundary>
            <ProvideAuth>
              <ElderlyPrivateRoute>
                <ErrorBoundary>
                  <ElderlyContentApp />
                </ErrorBoundary>
              </ElderlyPrivateRoute>
            </ProvideAuth>
          </ErrorBoundary>
        </DatesProvider>
      </MantineProvider>
    </ErrorBoundary>
  );
}

export default ElderlyApp;
